import { useState, useEffect, useRef } from "react";
import { UserName } from "@/components/badges/UserName";
import { useAuth } from "@/lib/auth";
import { useProfile } from "@/hooks/useProfile";
import { useThreadMessages, ThreadMessage } from "@/hooks/useMessageThreads";
import { useTypingIndicator } from "@/hooks/useTypingIndicator";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ArrowLeft, Send, Loader2, MoreVertical, Pencil, Trash2 } from "lucide-react";
import { format, differenceInMinutes } from "date-fns";
import { toast } from "sonner";

interface ThreadMessagingPanelProps {
  threadId: string;
  otherPerson: {
    id: string;
    full_name: string;
    avatar_url: string | null;
  };
  onBack?: () => void;
}

const EDIT_WINDOW_MINUTES = 15;

export function ThreadMessagingPanel({ threadId, otherPerson, onBack }: ThreadMessagingPanelProps) {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { messages, isLoading, sendMessage, markAsRead, editMessage, deleteMessage } = useThreadMessages(threadId);
  const { typingUsers, setTyping, stopTyping } = useTypingIndicator(threadId, profile?.full_name || "User");
  
  const [newMessage, setNewMessage] = useState("");
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mark unread messages as read
  useEffect(() => {
    if (!user?.id || !messages.length) return;

    const unreadMessages = messages.filter(
      (m) => m.sender_id !== user.id && !m.read_at
    );

    if (unreadMessages.length > 0) {
      markAsRead.mutate(unreadMessages.map((m) => m.id));
    }
  }, [messages, user?.id]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !threadId) return;

    try {
      stopTyping();
      await sendMessage.mutateAsync({ threadId, content: newMessage.trim() });
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewMessage(e.target.value);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    setTyping(true);
    
    typingTimeoutRef.current = setTimeout(() => {
      setTyping(false);
    }, 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const canEditMessage = (message: ThreadMessage) => {
    if (message.sender_id !== user?.id) return false;
    const minutesSinceSent = differenceInMinutes(new Date(), new Date(message.created_at));
    return minutesSinceSent <= EDIT_WINDOW_MINUTES;
  };

  const handleStartEdit = (message: ThreadMessage) => {
    setEditingMessageId(message.id);
    setEditContent(message.content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editContent.trim()) return;

    try {
      await editMessage.mutateAsync({ messageId: editingMessageId, content: editContent.trim() });
      setEditingMessageId(null);
      setEditContent("");
      toast.success("Message updated");
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDeleteClick = (messageId: string) => {
    setMessageToDelete(messageId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!messageToDelete) return;

    try {
      await deleteMessage.mutateAsync(messageToDelete);
      toast.success("Message deleted");
    } catch (error) {
      toast.error("Failed to delete message");
    } finally {
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  const otherTyping = typingUsers.filter((u) => u.id !== user?.id);

  return (
    <Card className="h-[calc(100vh-250px)] min-h-[350px] max-h-[650px] flex flex-col">
      <CardHeader className="border-b py-3 px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          {onBack && (
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          <UserAvatar
            avatarPath={otherPerson.avatar_url}
            fullName={otherPerson.full_name}
            className="h-10 w-10"
          />
          <CardTitle className="text-lg"><UserName userId={otherPerson.id} name={otherPerson.full_name} /></CardTitle>
        </div>
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        <ScrollArea className="flex-1 p-4" ref={scrollRef}>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              No messages yet. Start the conversation!
            </div>
          ) : (
            <div className="space-y-1">
              {messages.map((message, index) => {
                const isOwn = message.sender_id === user?.id;
                const sender = Array.isArray(message.sender) ? message.sender[0] : message.sender;
                
                // Check if this is first message in a consecutive group from same sender
                const prevMessage = index > 0 ? messages[index - 1] : null;
                const isFirstInGroup = !prevMessage || prevMessage.sender_id !== message.sender_id;

                return (
                  <div
                    key={message.id}
                    className={`flex gap-2 ${isOwn ? "justify-end" : "justify-start"} ${isFirstInGroup ? "mt-3" : "mt-0.5"}`}
                  >
                    {!isOwn && (
                      <div className="w-8 shrink-0">
                        {isFirstInGroup && (
                          <UserAvatar
                            avatarPath={sender?.avatar_url}
                            fullName={sender?.full_name}
                            className="h-8 w-8"
                            fallbackClassName="bg-muted text-muted-foreground"
                            thumbnailSize={64}
                          />
                        )}
                      </div>
                    )}
                    <div className={`max-w-[70%] ${isOwn ? "order-first" : ""}`}>
                      {editingMessageId === message.id ? (
                        <div className="space-y-2">
                          <Textarea
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="min-h-[60px]"
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={handleSaveEdit}>Save</Button>
                            <Button size="sm" variant="outline" onClick={handleCancelEdit}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div className="group relative">
                          <div
                            className={`rounded-lg px-3 py-2 ${
                              isOwn
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted"
                            }`}
                          >
                            <p className="text-sm whitespace-pre-wrap break-words">
                              {message.content}
                            </p>
                          </div>
                          <div className={`flex items-center gap-1 mt-1 ${isOwn ? "justify-end" : "justify-start"}`}>
                            <span className="text-xs text-muted-foreground">
                              {format(new Date(message.created_at), "h:mm a")}
                            </span>
                            {isOwn && canEditMessage(message) && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <MoreVertical className="h-3 w-3" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleStartEdit(message)}>
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteClick(message.id)}
                                    className="text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {isOwn && (
                      <div className="w-8 shrink-0">
                        {isFirstInGroup && (
                          <UserAvatar
                            avatarPath={profile?.avatar_url}
                            fullName={profile?.full_name}
                            className="h-8 w-8"
                            thumbnailSize={64}
                          />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Typing indicator */}
        {otherTyping.length > 0 && (
          <div className="px-4 py-1 text-sm text-muted-foreground">
            {otherTyping.map((u) => u.name).join(", ")} {otherTyping.length === 1 ? "is" : "are"} typing...
          </div>
        )}

        {/* Message input */}
        <div className="p-4 border-t">
          <div className="flex gap-2">
            <Textarea
              placeholder="Type a message..."
              value={newMessage}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              className="min-h-[44px] max-h-[120px] resize-none"
              rows={1}
            />
            <Button
              onClick={handleSend}
              disabled={!newMessage.trim() || sendMessage.isPending}
              className="shrink-0"
            >
              {sendMessage.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </CardContent>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete message?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The message will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
